import { use3DSFlow } from './frames/lib/3DS';
import { useCardOrder } from './frames/hooks/cardOrder';
import { CardinalEvents } from './frames/lib/3DS/constants';
import { useEffect, useRef } from 'react';
import { handleEvents, handleRedirect } from 'app/util/handleEvents';
import { logError } from 'app/util/errorsLoggerWrapper';
import { useCheckoutFrameStore } from 'app/context/checkoutStore';
import { getCardinal } from './frames/util/cardinal';
import { useAnalytics } from 'app/hooks/useAnalytics';
import { useIsEmbedded } from './frames/hooks/useIsEmbedded';
import { Order } from './frames/util/interface';
import { ThreeDSStep, traceSetupStep } from './frames/lib/3DS/trace';
import { markDdcCompleted } from './frames/lib/3DS/deviceData';

export const useThreeDsFlow = () => {
  const { initCardinal } = use3DSFlow();
  const { start3DSFlow, data } = useCardOrder();
  const order: Order = data?.order;
  const { checkoutRequest, entity } = useCheckoutFrameStore();
  const { isEmbedded } = useIsEmbedded();
  const { sendHide3DSChallengeDatalayer, sendShow3DSChallengeDatalayer, sendFinish3DSValidationDatalayer } =
    useAnalytics();
  // These listeners live on the shared window.Cardinal, so this hook detaches its own by
  // reference. use3DSFlow's dispose() used to clear them as a side effect of clearing its
  // own, which also meant it could clear a live flow's.
  const detachListeners = useRef<(() => void)[]>([]);

  const listenTo = (cardinal: any, event: CardinalEvents, handler: (payload: any) => void) => {
    cardinal.on(event, handler);
    detachListeners.current.push(() => {
      try {
        cardinal.off(event, handler);
      } catch (e) {
        logError('3DS: could not detach a Cardinal listener', { event, message: (e as Error)?.message });
      }
    });
  };

  const start3DSProcess = async () => {
    await initCardinal();
    const cardinal = getCardinal();
    const { companyId } = checkoutRequest ?? { companyId: '' };
    listenTo(cardinal, CardinalEvents.VALIDATED, (response: any) => {
      const { Validated, ErrorNumber } = response;
      if (!Validated) {
        logError('3DS: Card validation failed', { threeDsErrorCode: ErrorNumber, companyId });
        sendFinish3DSValidationDatalayer(false, ErrorNumber);
      }
    });
    listenTo(cardinal, CardinalEvents.SETUP_COMPLETE, () => {
      markDdcCompleted();
      traceSetupStep(ThreeDSStep.SETUP_COMPLETE, {
        checkoutId: checkoutRequest?.id ?? null,
        chargeId: checkoutRequest?.chargeId ?? null,
        entityName: entity?.name ?? null,
        companyId,
      });
      start3DSFlow();
    });
    listenTo(cardinal, CardinalEvents.UI_RENDER, () => sendShow3DSChallengeDatalayer());
    listenTo(cardinal, CardinalEvents.UI_CLOSE, () => sendHide3DSChallengeDatalayer());
  };

  useEffect(
    () => () => {
      detachListeners.current.forEach((detach) => detach());
      detachListeners.current = [];
    },
    [],
  );

  useEffect(() => {
    if (order) {
      if (isEmbedded) {
        handleEvents(order.charge.status, order);
      } else {
        handleRedirect(order.urlRedirect);
      }
    }
  }, [order]);

  return {
    start3DSProcess,
  };
};
